// src/data/loadConcepts.js
import Papa from "papaparse";
import { loadTranslations } from "./loadTranslations";

/**
 * Mappt das deutsche Type-Schema aus der CSV auf das POS-Schema (noun, verb, adjective, adverb, other)
 */
function mapTypeToPos(type) {
  if (!type) return "other";
  switch (type.toLowerCase().trim()) {
    case "substantiv":
    case "noun":
      return "noun";
    case "verb":
      return "verb";
    case "adjektiv":
    case "adjective":
      return "adjective";
    case "adverb":
      return "adverb";
    default:
      return "other";
  }
}

/**
 * Extrahiert Rangnummer aus ID, z. B. es-v-0043 → 43
 */
function extractRankFromId(id) {
  if (!id) return null;
  const parts = String(id).split("-");
  const num = parts[parts.length - 1];
  const n = parseInt(num, 10);
  if (Number.isNaN(n)) return null;
  return n;
}

/**
 * Häufigkeitsrang der Lernsprache aus public/data/frequency/<lang>.csv (id,rank, ohne Kopfzeile,
 * erzeugt mit scripts/vocab-pipeline/build-frequency-rank.js). Rückgabe: Map id → Rang oder null,
 * wenn es keine Datei gibt (der Dev-Server liefert dann index.html, deshalb Zeilenformat prüfen).
 */
export async function loadFrequencyRanks(langCode) {
  if (!langCode) return null;
  try {
    const response = await fetch(`/data/frequency/${langCode}.csv`);
    if (!response.ok) return null;
    const ranks = new Map();
    for (const line of (await response.text()).split(/\r?\n/)) {
      const match = line.trim().match(/^([\w-]+),(\d+)$/);
      if (match) ranks.set(match[1], Number(match[2]));
    }
    return ranks.size > 0 ? ranks : null;
  } catch (e) {
    console.warn(`[Fluent] Rang-Datei für ${langCode} nicht geladen:`, e);
    return null;
  }
}

/**
 * Setzt freqRank (global) und freqRankType (Platz innerhalb der Wortart) nach einer Rang-Datei.
 * Concepts ohne Eintrag kommen hinter alle anderen, in ihrer bisherigen Reihenfolge.
 */
export function applyFrequencyRanks(concepts, ranks) {
  if (!ranks) return concepts;
  const sorted = [...concepts].sort(
    (a, b) => (ranks.get(a.id) ?? Infinity) - (ranks.get(b.id) ?? Infinity) || a.freqRank - b.freqRank
  );
  const perPos = {};
  const byId = new Map(
    sorted.map((c, i) => {
      perPos[c.pos] = (perPos[c.pos] || 0) + 1;
      return [c.id, { freqRank: i + 1, freqRankType: perPos[c.pos] }];
    })
  );
  return concepts.map((c) => ({ ...c, ...byId.get(c.id) }));
}

/**
 * Lädt die sprachneutralen Konzepte (id, type, category) aus public/data/concepts.csv.
 * Übersetzungen kommen separat über loadTranslations(langCode).
 *
 * rankLang (Lernsprache): Reihenfolge nach deren Rang-Datei. Ohne Datei gilt die alte Reihenfolge
 * (Zeile in concepts.csv bzw. Nummer in der ID = spanischer Rang von früher).
 */
export async function loadConcepts(rankLang = null) {
  const response = await fetch("/data/concepts.csv");
  if (!response.ok) {
    console.error("[Fluent] Konnte /data/concepts.csv nicht laden:", response.status, response.statusText);
    return [];
  }

  const text = await response.text();
  const parsed = Papa.parse(text, { header: false, skipEmptyLines: true, quoteChar: '"', delimiter: "," });
  const rows = parsed.data || [];

  const concepts = rows
    .map((cols, index) => {
      if (!cols || cols.length < 3) return null;

      const id = cols[0]?.trim();
      const type = cols[1]?.trim() || "other";
      const category = cols[2]?.trim() || "nocategory";

      if (!id) return null;

      return {
        id,
        type,
        category,
        pos: mapTypeToPos(type),
        freqRank: index + 1,
        freqRankType: extractRankFromId(id) ?? index + 1,
      };
    })
    .filter(Boolean);

  console.log("[Fluent] Concepts geladen:", concepts.length);
  const ranks = await loadFrequencyRanks(rankLang);
  return applyFrequencyRanks(concepts, ranks);
}

/**
 * Funktionswörter einer Sprache: die Übersetzungen aller Concepts vom Typ "other"
 * (Pronomen, Präpositionen, Konjunktionen, Fragewörter …), als einzelne Wörter in
 * Kleinbuchstaben ("a veces" → "a", "veces"). Rückgabe: Set.
 */
export async function loadFunctionWords(langCode) {
  const [concepts, translations] = await Promise.all([
    loadConcepts(),
    loadTranslations(langCode),
  ]);

  const words = new Set();
  for (const concept of concepts) {
    if (concept.type !== "other") continue;
    const text = translations.get(concept.id) || "";
    for (const part of text.toLowerCase().split(/\s+/)) {
      if (part) words.add(part);
    }
  }
  return words;
}
